import { useEffect, useMemo, useState } from "react";
import { FileText, Loader2, XIcon } from "lucide-react";
import { openPuckDrawer, closePuckDrawer } from "@/components/cms-puck/fields/puck-drawer-store";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import { FileDropzone } from "@/components/ui/FileDropzone";
import { Thumbnail } from "@/components/ui/tumbnail";
import { CloudUpload } from "lucide-react";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useGetFileByDocId, useGetFileByTag } from "@/api/file";
import { uploadDocument } from "@/api/image";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * Custom field Puck pour le bloc `PdfReader` : choisit UN PDF dans la table
 * `File` (système « documents », PAS la médiathèque image). Deux onglets :
 *  - « Bibliothèque » : PDF du CMS de l'instance (scopés `docId = CMS.id`) ;
 *  - « Téléverser » : upload d'un PDF via le convertisseur (route `/document`,
 *    qui accepte déjà le PDF) → callback `file/create`. Stocke un `fileId`.
 */

const PDF_ACCEPT: Record<string, string[]> = { "application/pdf": [".pdf"] };

type CmsFile = {
  id: string;
  title?: string;
  mimetype?: string;
  tag?: string;
};

function isPdf(f: CmsFile): boolean {
  return (f.mimetype ?? "").toLowerCase().includes("pdf");
}

function FileRow({
  file,
  selected,
  onSelect,
}: {
  file: CmsFile;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded border p-2 text-left ${
        selected
          ? "border-primary ring-2 ring-primary/40"
          : "border-slate-200 hover:border-slate-300"
      }`}
      title={file.title || file.id}
    >
      <FileText className="h-5 w-5 shrink-0 text-slate-500" />
      <span className="line-clamp-1 text-sm text-slate-700">
        {file.title || file.id}
      </span>
    </button>
  );
}

/** Contenu du drawer (monté dans le HOST → state stable). */
function PdfPickerBody({
  cmsId,
  instanceId,
  s3Bucket,
  selectedId,
  onPick,
}: {
  cmsId: string;
  instanceId: string;
  s3Bucket: string;
  selectedId: string | undefined;
  onPick: (fileId: string) => void;
}) {
  const cmsNew = useCmsNew();
  const [tab, setTab] = useState<"library" | "upload">("library");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingTag, setPendingTag] = useState<string>("");

  const {
    data: libraryRaw,
    isLoading,
    refetch,
  } = useGetFileByDocId(cmsId, { orderBy: "createdAt", orderDirection: "desc" }, {
    page: 0,
    pageSize: 100,
  });

  const files: CmsFile[] = useMemo(() => {
    const list = Array.isArray(libraryRaw)
      ? libraryRaw
      : ((libraryRaw as { data?: CmsFile[] } | undefined)?.data ?? []);
    const trimmed = search.trim().toLowerCase();
    return list
      .filter(isPdf)
      .filter((f) =>
        trimmed ? (f.title ?? "").toLowerCase().includes(trimmed) : true,
      );
  }, [libraryRaw, search]);

  // Poll par tag après upload : le fichier est créé par le callback du
  // convertisseur (asynchrone) → on l'attend puis on le sélectionne.
  const { data: uploadedByTag } = useGetFileByTag(pendingTag, instanceId);
  useEffect(() => {
    if (!pendingTag || !uploadedByTag?.id) return;
    const id = uploadedByTag.id as string;
    setPendingTag("");
    setUploading(false);
    refetch();
    onPick(id);
    toast({ variant: "success", description: cmsNew("cms.new.auto.description.pdf_ajoute", "PDF ajouté"), duration: 1200 });
    // onPick/refetch sont stables pour cet usage ; on réagit à l'arrivée du fichier.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTag, uploadedByTag]);

  const onDrop = async (picked: File | undefined) => {
    if (!picked) return;
    const tag = `cms-pdf-${crypto.randomUUID()}`;
    try {
      setUploading(true);
      await uploadDocument(picked, s3Bucket, {
        sourceName: picked.name,
        title: picked.name.replace(/\.pdf$/i, ""),
        tag,
        docId: cmsId,
        mimetype: picked.type || "application/pdf",
        instanceId,
      });
      // Déclenche le polling par tag (le fichier apparaît après le callback).
      setPendingTag(tag);
    } catch (e) {
      console.error(e);
      setUploading(false);
      toast({
        variant: "error",
        description: cmsNew("cms.new.auto.description.echec_du_televersement_du_pdf", "Échec du téléversement du PDF"),
        duration: 2000,
      });
    }
  };

  return (
    <div className="space-y-3 pt-4">
        <div className="inline-flex gap-1 rounded-lg bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setTab("library")}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === "library"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {cmsNew("cms.new.auto.text.bibliotheque", "Bibliothèque")}
          </button>
          <button
            type="button"
            onClick={() => setTab("upload")}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === "upload"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {cmsNew("cms.new.pickers.pdf.uploadTab", "Téléverser")}
          </button>
        </div>

        {tab === "library" ? (
          <>
            <SearchBar
              search={search}
              onChangeSearch={setSearch}
              placeholder={cmsNew("cms.new.auto.placeholder.rechercher_un_pdf", "Rechercher un PDF…")}
            />
            {isLoading ? (
              <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
            ) : files.length === 0 ? (
              <p className="text-sm text-slate-500">
                {cmsNew(
                  "cms.new.pickers.pdf.emptyLibrary",
                  "Aucun PDF. Utilisez l'onglet « Téléverser ».",
                )}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {files.map((f) => (
                  <FileRow
                    key={f.id}
                    file={f}
                    selected={selectedId === f.id}
                    onSelect={() => {
                      onPick(f.id);
                      closePuckDrawer();
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-3">
            {uploading ? (
              <div className="flex items-center gap-2 rounded border border-slate-200 p-4 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {cmsNew("cms.new.pickers.pdf.uploading", "Téléversement en cours…")}
              </div>
            ) : (
              <FileDropzone
                multiple={false}
                accept={PDF_ACCEPT}
                onDrop={(f: File[]) => onDrop(f[0])}
                className="bg-white border border-solid"
              >
                {() => (
                  <div className="flex flex-col items-center justify-center gap-4 py-6">
                    <Thumbnail>
                      <CloudUpload className="size-6 text-gray-700" />
                    </Thumbnail>
                    <span className="text-sm text-slate-600">
                      {cmsNew("cms.new.pickers.pdf.dropPdf", "Déposez un fichier PDF")}
                    </span>
                  </div>
                )}
              </FileDropzone>
            )}
          </div>
        )}
    </div>
  );
}

export function PdfPickerField({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
}) {
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;
  const instanceId = instanceData?.id;
  const s3Bucket = instanceData?.s3Bucket;

  const fileId = value || undefined;
  const { data: libraryRaw } = useGetFileByDocId(cmsId ?? "");
  const selectedTitle = useMemo(() => {
    if (!fileId) return undefined;
    const list = Array.isArray(libraryRaw)
      ? libraryRaw
      : ((libraryRaw as { data?: CmsFile[] } | undefined)?.data ?? []);
    return (list as CmsFile[]).find((f) => f.id === fileId)?.title;
  }, [libraryRaw, fileId]);

  if (!cmsId || !instanceId || !s3Bucket) {
    return (
      <p className="p-1 text-xs text-slate-400">{cmsNew("cms.new.auto.text.cms_non_disponible", "CMS non disponible.")}</p>
    );
  }

  return (
    <div className="space-y-2 p-1">
      <div className="text-xs font-medium text-slate-600">{cmsNew("cms.new.auto.text.document_pdf", "Document PDF")}</div>
      {fileId ? (
        <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 py-1 pl-2 pr-2">
          <FileText className="h-4 w-4 text-slate-500" />
          <span className="max-w-[180px] truncate text-xs text-slate-600">
            {selectedTitle || `PDF ${fileId.slice(0, 6)}`}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
            title={cmsNew("cms.new.auto.title.retirer", "Retirer")}
            aria-label={cmsNew("cms.new.auto.label.retirer_le_pdf", "Retirer le PDF")}
          >
            <XIcon size={14} />
          </button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">{cmsNew("cms.new.auto.text.aucun_pdf_selectionne", "Aucun PDF sélectionné.")}</p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          openPuckDrawer({
            title: cmsNew("cms.new.auto.title.choisir_un_pdf", "Choisir un PDF"),
            maxWidth: "sm:max-w-2xl",
            node: (
              <PdfPickerBody
                cmsId={cmsId}
                instanceId={instanceId}
                s3Bucket={s3Bucket}
                selectedId={fileId}
                onPick={(id) => onChange(id)}
              />
            ),
          })
        }
      >
        {fileId
          ? cmsNew("cms.new.pickers.pdf.changePdf", "Changer le PDF")
          : cmsNew("cms.new.auto.title.choisir_un_pdf", "Choisir un PDF")}
      </Button>
    </div>
  );
}
